import { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { PublicPage, PageHero, Seo } from "@/components/public/PublicChrome";

const FAQS = [
  { cat: "Getting Started", q: "How does the 7-day free trial work?", a: "Choose any plan and start a 7-day free trial with no credit card required. You get full access to that plan's features. Cancel anytime during the trial and you won't be charged." },
  { cat: "Getting Started", q: "Do I need a credit card to try Fairview?", a: "No. You can start your free trial with just an email — no credit card needed." },
  { cat: "Getting Started", q: "Can I explore before signing up?", a: "Yes. Our Interactive Demo lets you click through every module with sample data, no account required." },
  { cat: "Billing", q: "What happens when my trial ends?", a: "You'll be prompted to subscribe to keep full access. Your data, documents, progress, and completed courses are always preserved." },
  { cat: "Billing", q: "Can I cancel anytime?", a: "Yes. You can cancel future renewals at any time and keep access until the end of your current billing period." },
  { cat: "Billing", q: "Are subscriptions refundable?", a: "Paid subscription fees are generally non-refundable, but refunds may be issued for duplicate charges, billing errors, or verified technical issues. See our Refund & Cancellation Policy." },
  { cat: "Product", q: "What's included in the platform?", a: "Business Formation, Business Credit, Funding, an AI Advisor, Education & Training, a Resource Center, Document Library, Contracts, a Progress Tracker, and more — all integrated." },
  { cat: "Product", q: "Is the AI Advisor really personalized?", a: "Yes. It reads your progress across formation, credit, funding, documents, and courses to recommend your next best action and explain why it matters." },
  { cat: "Security", q: "How is my data protected?", a: "We use encryption in transit, access controls, and short-lived signed links for document downloads. Payments are processed securely by Stripe." },
  { cat: "Security", q: "Who can see my documents?", a: "Only you. Document access is scoped to your account, and share links are time-limited and revocable by expiry." },
];

function Item({ f, open, onToggle }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#121214] overflow-hidden">
      <button onClick={onToggle} data-testid={`faq-q-${f.q.slice(0, 12)}`}
        className="w-full flex items-center justify-between gap-4 p-5 text-left">
        <span className="text-white font-medium">{f.q}</span>
        <ChevronDown className={`w-5 h-5 text-[#D4AF37] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm text-[#A1A1AA] leading-relaxed">{f.a}</p>}
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  const groups = useMemo(() => {
    const cats = [...new Set(FAQS.map((f) => f.cat))];
    return cats.map((cat) => ({
      cat,
      items: FAQS.map((f, idx) => ({ ...f, idx })).filter((f) => f.cat === cat),
    }));
  }, []);
  return (
    <PublicPage>
      <Seo title="FAQ" path="/faq" description="Find answers to common questions about Fairview Systems — how our business tools and features work, what each plan includes, billing, free trials, and security." />
      <PageHero eyebrow="FAQ" title="Frequently asked questions"
        subtitle="Everything you need to know about trials, billing, features, and security." />
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto space-y-10" data-testid="faq-list">
          {groups.map((g) => (
            <div key={g.cat}>
              <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-4">{g.cat}</div>
              <div className="space-y-3">
                {g.items.map((f) => (
                  <Item key={f.q} f={f} open={open === f.idx} onToggle={() => setOpen(open === f.idx ? -1 : f.idx)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </PublicPage>
  );
}
